import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../PayRent/PayRentPage.css';

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

// Standalone, public page — a tenant opens this from a link the owner shares
// (WhatsApp/SMS), no account or login required. Rendered outside the normal
// app shell (see App.js), so it has no Navbar/context dependency.
export default function AgreementPage({ tenantId }) {
  const [loading, setLoading]       = useState(true);
  const [info, setInfo]             = useState(null);
  const [error, setError]           = useState('');
  const [name, setName]             = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    axios.get(`/api/tenants/public/${tenantId}`)
      .then(res => { if (res.data.success) setInfo(res.data.data); else setError('This link is invalid or has expired.'); })
      .catch(() => setError('This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async () => {
    if (!name.trim()) { setError('Please type your full name to acknowledge.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await axios.post(`/api/tenants/public/${tenantId}/agreement/ack`, { name: name.trim() });
      if (!data.success) throw new Error(data.message);
      setInfo(prev => ({ ...prev, tenantAckAt: data.data.tenant_ack_at, tenantAckName: data.data.tenant_ack_name }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not submit acknowledgment.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="pay-rent-page">
        <div className="pay-rent-card">
          <div className="spinner" style={{ width: 32, height: 32, borderColor: '#eee', borderTopColor: '#E53935', margin: '40px auto' }} />
        </div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="pay-rent-page">
        <div className="pay-rent-card">
          <div className="pay-rent-icon">⚠️</div>
          <h2>Link Not Valid</h2>
          <p className="pay-rent-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pay-rent-page">
      <div className="pay-rent-card agreement-card">
        <div className="pay-rent-logo" style={{ textAlign: 'center' }}>📍 Spaczo</div>
        <h2>Rental Agreement (Reference Copy)</h2>
        <div className="agreement-text">
          <p>
            This document records the rental terms agreed between the owner and tenant of the property below,
            for reference and mutual clarity. It is <strong>not a substitute</strong> for a registered/stamped
            rental agreement where required under state law.
          </p>
          <ul>
            <li><strong>Property:</strong> {info.propertyTitle}{info.propertyAddress ? `, ${info.propertyAddress}` : ''}</li>
            <li><strong>Owner:</strong> {info.ownerName}</li>
            <li><strong>Tenant:</strong> {info.tenantName}</li>
            <li><strong>Move-in date:</strong> {fmtDate(info.moveInDate)}</li>
            <li><strong>Monthly rent:</strong> {info.monthlyRent ? `₹${Number(info.monthlyRent).toLocaleString('en-IN')}` : '—'}</li>
            <li><strong>Security deposit:</strong> {info.securityDeposit ? `₹${Number(info.securityDeposit).toLocaleString('en-IN')}` : '—'}</li>
            <li><strong>Notice period:</strong> 30 days' written notice by either party before vacating or termination.</li>
            <li><strong>Maintenance:</strong> The owner is responsible for structural repairs; the tenant is responsible for day-to-day upkeep and reporting issues promptly.</li>
            <li><strong>Deposit refund:</strong> Refundable within a reasonable period after move-out, less any deductions for damage beyond normal wear and tear.</li>
            <li><strong>Sub-letting:</strong> Not permitted without the owner's written consent.</li>
          </ul>
        </div>

        <div className="agreement-ack-row">
          <span>Owner</span>
          <strong>{info.ownerAckAt ? `✓ Acknowledged ${fmtDate(info.ownerAckAt)}` : 'Not yet acknowledged'}</strong>
        </div>
        <div className="agreement-ack-row">
          <span>Tenant</span>
          <strong>{info.tenantAckAt ? `✓ Acknowledged by ${info.tenantAckName} on ${fmtDate(info.tenantAckAt)}` : 'Not yet acknowledged'}</strong>
        </div>

        {!info.tenantAckAt && (
          <div className="agreement-ack-form">
            <input
              className="form-input"
              placeholder="Type your full name to acknowledge"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            {error && <div className="pay-rent-error">{error}</div>}
            <button className="pay-rent-btn" onClick={acknowledge} disabled={submitting}>
              {submitting ? 'Submitting…' : 'I Agree & Acknowledge'}
            </button>
          </div>
        )}

        <button className="btn btn-secondary" style={{ marginTop: 14, width: '100%' }} onClick={() => window.print()}>
          🖨️ Print / Save as PDF
        </button>
        <p className="pay-rent-note">
          This reference agreement is generated from details entered by the property owner on Spaczo, to reduce
          disputes — it does not replace a government-registered agreement where legally required.
        </p>
      </div>
    </div>
  );
}
